import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Box, Typography, Divider, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

const EMPTY_FORM = { dataType: '', retentionPeriod: '10 years', anonymization: '', legalBasis: '', description: '' };

const DEFAULT_POLICIES = [
  { id: 1, dataType: 'Student Personal Records', retentionPeriod: '10 years', anonymization: 'After retention period', legalBasis: 'Education regulations', description: 'All student personal data including names, contacts, and demographic information.' },
  { id: 2, dataType: 'Student Academic Records', retentionPeriod: '10 years', anonymization: 'After retention period', legalBasis: 'Education regulations', description: 'Grades, report cards, transcripts, and exam results.' },
  { id: 3, dataType: 'Student Enrollment Records', retentionPeriod: '10 years', anonymization: 'After retention period', legalBasis: 'Education regulations', description: 'Class enrollment, attendance, and progression history.' },
  { id: 4, dataType: 'Student Financial Records', retentionPeriod: '10 years', anonymization: 'After retention period', legalBasis: 'Financial regulations', description: 'Fee payments, invoices, and financial aid records.' },
  { id: 5, dataType: 'Student Health Records', retentionPeriod: '10 years', anonymization: 'After retention period', legalBasis: 'Health data regulations', description: 'Medical records, allergies, and health screening data.' },
  { id: 6, dataType: 'Student Disciplinary Records', retentionPeriod: '10 years', anonymization: 'After retention period', legalBasis: 'Education regulations', description: 'Behavioral incidents, suspensions, and counseling records.' },
];

export default function DataPolicyPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const canEdit = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('system-settings');
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJson('/api/super-admin/data-policies');
      const loaded = Array.isArray(data?.policies) ? data.policies : [];
      // If no policies exist yet, initialize with defaults
      if (loaded.length === 0) {
        if (canEdit) {
          await fetchJson('/api/super-admin/data-policies', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ policies: DEFAULT_POLICIES })
          });
        }
        setPolicies(DEFAULT_POLICIES);
      } else {
        setPolicies(loaded);
      }
    } catch (e) {
      setError(e?.message || t('dataPolicy.loadError', 'Failed to load data policies'));
    } finally {
      setLoading(false);
    }
  }, [canEdit, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    if (!canEdit) return;
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    if (!canEdit) return;
    setEditing(row);
    setForm({ dataType: row.dataType, retentionPeriod: row.retentionPeriod, anonymization: row.anonymization, legalBasis: row.legalBasis || '', description: row.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      let updated;
      if (editing) {
        updated = policies.map(p => p.id === editing.id ? { ...p, ...form } : p);
      } else {
        const newId = policies.length > 0 ? Math.max(...policies.map(p => p.id || 0)) + 1 : 1;
        updated = [...policies, { id: newId, ...form }];
      }
      await fetchJson('/api/super-admin/data-policies', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policies: updated })
      });
      setPolicies(updated);
      setSnack('Policy saved');
      setDialogOpen(false);
    } catch (e) {
      setError(e?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!canEdit) return;
    try {
      const updated = policies.filter(p => p.id !== id);
      await fetchJson('/api/super-admin/data-policies', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policies: updated })
      });
      setPolicies(updated);
      setSnack('Policy removed');
    } catch (e) {
      setError(e?.message || 'Failed to delete');
    }
  };

  const columns = [
    { field: 'dataType', headerName: 'Data Type', flex: 1 },
    { field: 'retentionPeriod', headerName: 'Retention Period', flex: 1 },
    { field: 'anonymization', headerName: 'Anonymization', flex: 1 },
    { field: 'legalBasis', headerName: 'Legal Basis', flex: 1 },
    { field: 'actions', headerName: 'Actions', width: 180, renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={() => openEdit(params.row)} disabled={!canEdit}>Edit</Button>
        <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(params.row.id)} disabled={!canEdit}>Delete</Button>
      </Stack>
    ) },
  ];

  return (
    <Box width="100%" my={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={2}>
        <Typography variant="h5" fontWeight={700}>{t('dataPolicy.title') || 'Data Policy & Compliance'}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!canEdit}>Add Policy</Button>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('dataPolicy.desc', 'Define how long student records are kept in the system before automatic deletion. Records are tracked from the year they were added. Default retention is 10 years.')}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <div style={{ height: 400, width: '100%' }}>
        <DataGrid rows={policies} columns={columns} loading={loading} pageSize={10} rowsPerPageOptions={[10, 25]} disableSelectionOnClick />
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Policy' : 'Add Policy'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Data Type" value={form.dataType} onChange={e => setForm(f => ({ ...f, dataType: e.target.value }))} fullWidth required />
            <TextField label="Retention Period" value={form.retentionPeriod} onChange={e => setForm(f => ({ ...f, retentionPeriod: e.target.value }))} fullWidth placeholder="e.g. 7 years" />
            <TextField label="Anonymization Rule" value={form.anonymization} onChange={e => setForm(f => ({ ...f, anonymization: e.target.value }))} fullWidth placeholder="e.g. After graduation" />
            <TextField label="Legal Basis" value={form.legalBasis} onChange={e => setForm(f => ({ ...f, legalBasis: e.target.value }))} fullWidth placeholder="e.g. GDPR Art. 6" />
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.dataType || !canEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
